import { Column, Row, Select, AddBoxIcon } from "@hightouch/htui";

import { useFilterableOptionsContext } from "src/components/explore/filter-popover/filterable-options-hook";
import {
  createSubconditionUpdater,
  removeSubconditionMutative,
} from "src/components/explore/utils/condition-builders";
import { isAndOrCondition } from "src/components/explore/utils/type-guards";
import { splitArrayItemColumnName } from "src/pages/schema/columns/object-array-columns";
import {
  ColumnType,
  ConditionType,
  DefaultOperators,
  PropertyCondition,
  isColumnReference,
} from "src/types/visual";

import { DetailButton, AndOrToggleButton, XButton } from "./condition-buttons";
import type { FilterProps } from "./condition";
import { PropertyOperatorValueFilter } from "./property-operator-value-filter";
import { usePropertyFilterValidation } from "./property-filter";
import { getModelIdFromColumn, getPropertyNameFromColumn } from "./utils";

type ObjectArrayItemFiltersProps = Pick<
  FilterProps<PropertyCondition>,
  "condition" | "onUpdate" | "parent" | "isDisabled"
>;

export const ObjectArrayItemFilters = ({
  condition,
  onUpdate,
  parent,
  isDisabled,
}: ObjectArrayItemFiltersProps) => {
  const { columns } = useFilterableOptionsContext();
  const arrayPath = getPropertyNameFromColumn(condition.property);
  const modelId =
    (isColumnReference(condition.property)
      ? getModelIdFromColumn(condition.property)
      : null) ?? "";
  const itemColumns = (columns ?? []).flatMap((column) => {
    const split = splitArrayItemColumnName(column.name);
    if (!split || split.arrayPath !== arrayPath) {
      return [];
    }
    return [
      {
        relativePath: split.relativePath,
        propertyType: (column.custom_type ?? column.type) as ColumnType,
      },
    ];
  });
  const group = condition.subconditions?.[0];
  const predicates = isAndOrCondition(group) ? group.conditions : [];
  const groupType = isAndOrCondition(group) ? group.type : ConditionType.And;
  const generateUpdater = createSubconditionUpdater(onUpdate);

  const addPredicate = () => {
    const field = itemColumns[0];
    if (!field) {
      return;
    }
    onUpdate((current) => {
      const existing = current.subconditions?.[0];
      const top = isAndOrCondition(existing)
        ? existing
        : {
            id: crypto.randomUUID(),
            type: ConditionType.And as const,
            conditions: [],
          };
      current.subconditions = [
        {
          ...top,
          conditions: [
            ...top.conditions,
            {
              id: crypto.randomUUID(),
              type: ConditionType.Property,
              property: {
                type: "raw",
                name: field.relativePath,
                modelId,
              },
              propertyType: field.propertyType,
              operator: DefaultOperators[field.propertyType],
              value: null,
            },
          ],
        },
      ];
    });
  };

  return (
    <Column gap={2} pl={8}>
      {predicates.map((predicate, index) => (
        <ItemPredicate
          key={predicate.id ?? index}
          condition={predicate}
          isDisabled={isDisabled}
          isFirst={index === 0}
          groupType={groupType}
          itemColumns={itemColumns}
          modelId={modelId}
          parent={parent}
          onUpdate={generateUpdater(predicate.id)}
          onRemove={() =>
            onUpdate((current) =>
              removeSubconditionMutative(current, predicate.id),
            )
          }
          onToggleGroup={() =>
            onUpdate((current) => {
              const existing = current.subconditions?.[0];
              if (!isAndOrCondition(existing)) {
                return;
              }
              current.subconditions = [
                {
                  ...existing,
                  type:
                    existing.type === ConditionType.And
                      ? ConditionType.Or
                      : ConditionType.And,
                },
              ];
            })
          }
        />
      ))}
      <DetailButton
        isDisabled={isDisabled || itemColumns.length === 0}
        icon={AddBoxIcon}
        size="sm"
        onClick={addPredicate}
      >
        {itemColumns.length === 0
          ? "Add item fields on the event column"
          : "Add item condition"}
      </DetailButton>
    </Column>
  );
};

const ItemPredicate = ({
  condition,
  isDisabled,
  isFirst,
  groupType,
  itemColumns,
  modelId,
  parent,
  onUpdate,
  onRemove,
  onToggleGroup,
}: {
  condition: PropertyCondition;
  isDisabled?: boolean;
  isFirst: boolean;
  groupType: ConditionType.And | ConditionType.Or;
  itemColumns: { relativePath: string; propertyType: ColumnType }[];
  modelId: string;
  parent: ObjectArrayItemFiltersProps["parent"];
  onUpdate: (updater: (condition: PropertyCondition) => void) => void;
  onRemove: () => void;
  onToggleGroup: () => void;
}) => {
  const { operatorError, valueError } = usePropertyFilterValidation(condition, {
    isRealtimeAudience: true,
  });
  const selected = getPropertyNameFromColumn(condition.property) ?? "";

  return (
    <Row gap={2} alignItems="flex-start">
      {isFirst ? null : (
        <AndOrToggleButton
          isDisabled={isDisabled}
          conditionType={groupType}
          onClick={onToggleGroup}
        />
      )}
      <Select
        aria-label="Item field"
        isDisabled={isDisabled}
        options={itemColumns.map((column) => ({
          label: column.relativePath,
          value: column.relativePath,
        }))}
        value={selected}
        width="auto"
        onChange={(relativePath) => {
          const field = itemColumns.find(
            (column) => column.relativePath === relativePath,
          );
          if (!field || !relativePath) {
            return;
          }
          onUpdate((current) => {
            current.property = {
              type: "raw",
              name: relativePath,
              modelId,
            };
            current.propertyType = field.propertyType;
            current.operator = DefaultOperators[field.propertyType];
            current.value = null;
          });
        }}
      />
      <PropertyOperatorValueFilter
        condition={condition}
        isDisabled={isDisabled}
        loadingSuggestions={false}
        operatorError={operatorError}
        parent={parent}
        suggestions={[]}
        allowPropertyComparison={false}
        valueError={valueError}
        onUpdate={onUpdate}
      />
      <XButton isDisabled={isDisabled} onRemove={onRemove} />
    </Row>
  );
};
